import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  APIProvider,
  Map,
  useMap
} from '@vis.gl/react-google-maps';

// --- CONFIGURATION ---
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const LOOP_DURATION = 18000;

// --- CUSTOM STYLES: AGGRESSIVELY HIDE EVERYTHING ---
const MINIMAL_MAP_STYLES = [
  { featureType: "all", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "landscape", elementType: "labels", stylers: [{ visibility: "off" }] }
];

// --- ICONS ---
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const HomeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="none">
    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
  </svg>
);

const AlertIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="none" style={{ fontWeight: 800 }}>
    <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
  </svg>
);

const QuestionIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="none" style={{ fontWeight: 800 }}>
    <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"/>
  </svg>
);

const CraneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 22h16"></path>
    <path d="M14 2L9 22"></path>
    <path d="M10 6l4-4 8 4-8 4"></path>
  </svg>
);

// --- HELPER: HTMLOverlay ---
const HTMLMarker = ({ position, children, zIndex = 0 }: { position: google.maps.LatLngLiteral, children: React.ReactNode, zIndex?: number }) => {
  const map = useMap();
  // Plain DOM node owned by us, not by React's tree. Google Maps moves this node
  // between panes and removes it on teardown; React only ever manages the children
  // portalled *into* it. Sharing one node between both caused a double-removal
  // (NotFoundError: removeChild) on unmount.
  const [container] = useState(() => document.createElement('div'));
  const overlayRef = useRef<google.maps.OverlayView | null>(null);

  const positionRef = useRef(position);
  positionRef.current = position;

  useEffect(() => {
    container.style.position = 'absolute';
    container.style.cursor = 'pointer';
    container.style.transform = 'translate(-50%, -100%)';
    container.style.zIndex = String(zIndex);
  }, [container, zIndex]);

  useEffect(() => {
    if (!map) return;

    const ov = new google.maps.OverlayView();
    ov.onAdd = () => {
      ov.getPanes()?.floatPane.appendChild(container);
    };
    ov.onRemove = () => {
      container.parentNode?.removeChild(container);
    };
    ov.draw = () => {
      const projection = ov.getProjection();
      if (!projection) return;

      const point = projection.fromLatLngToDivPixel(positionRef.current);
      if (point) {
        container.style.left = point.x + 'px';
        container.style.top = point.y + 'px';
      }
    };
    ov.setMap(map);
    overlayRef.current = ov;

    return () => {
      ov.setMap(null);
      overlayRef.current = null;
    };
  }, [map, container]);

  useEffect(() => {
    overlayRef.current?.draw();
  }, [position]);

  return createPortal(children, container);
};

// --- MAP CAMERA ANIMATOR ---
const CameraAnimator = ({ active }: { active: boolean }) => {
  const map = useMap();
  const frameRef = useRef<number>(0);
  
  useEffect(() => {
    if (!map || !active) return;
    
    // --- SETTINGS ---
    const TARGET_LAT = 40.9275; // Centered on Home
    const TARGET_LNG = -73.9983;
    const START_OFFSET_LNG = -0.15; 
    
    const TYPE_DURATION = 2000; 
    const PAN_DURATION = 2000;  
    const DRIFT_SPEED = 0.000003;

    // 1. Initial Position (Zoom 12.8)
    map.setZoom(12.8);
    map.moveCamera({ center: { lat: TARGET_LAT, lng: TARGET_LNG + START_OFFSET_LNG } });

    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      
      if (elapsed < TYPE_DURATION) {
        // Phase 1: Hold Still
        frameRef.current = requestAnimationFrame(animate);
      } 
      else if (elapsed < (TYPE_DURATION + PAN_DURATION)) {
        // Phase 2: Fast Pan
        const panProgress = (elapsed - TYPE_DURATION) / PAN_DURATION;
        const ease = panProgress < 0.5 
          ? 2 * panProgress * panProgress 
          : 1 - Math.pow(-2 * panProgress + 2, 2) / 2;
        
        const currentLng = (TARGET_LNG + START_OFFSET_LNG) + (Math.abs(START_OFFSET_LNG) * ease);
        map.moveCamera({ center: { lat: TARGET_LAT, lng: currentLng } });
        frameRef.current = requestAnimationFrame(animate);
      } 
      else {
        // Phase 3: Drift
        const driftTime = elapsed - (TYPE_DURATION + PAN_DURATION);
        const driftLng = TARGET_LNG + (driftTime * (DRIFT_SPEED / 16));
        map.moveCamera({ center: { lat: TARGET_LAT, lng: driftLng } });
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [map, active]);

  return null;
};

// --- MAIN COMPONENT ---
const LiveDemoMap = () => {
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setResetKey(prev => prev + 1);
    }, LOOP_DURATION);
    return () => clearInterval(interval);
  }, []);

  // --- COORDINATES ---
  const POS_HOME   = { lat: 40.9275, lng: -73.9983 }; 
  const POS_RISK   = { lat: 40.9120, lng: -73.9850 }; 
  const POS_VOTE   = { lat: 40.9450, lng: -73.9750 }; 
  const POS_ACTIVE = { lat: 40.9200, lng: -74.0350 }; 

  return (
    <APIProvider apiKey={API_KEY}>
      <div className="live-demo-container" style={{ position: 'relative', width: '100%', height: '600px', overflow: 'hidden', backgroundColor: '#e5e7eb' }}>
        
        <style>{`
          /* --- HIDE GOOGLE UI (Logo, Terms, Shortcuts) --- */
          .gm-style-cc { display: none !important; }
          .gmnoprint { display: none !important; }
          a[href^="https://maps.google.com/maps"] { display: none !important; }
          a[target="_blank"] { display: none !important; }
          .gm-style a img { display: none !important; } /* Aggressive Logo Hide */

          @keyframes typing {
            from { width: 0 }
            to { width: 100% }
          }

          @keyframes pinDrop {
            0% { transform: translateY(-600px) scale(0); opacity: 0; }
            60% { transform: translateY(20px) scale(1.1); opacity: 1; }
            80% { transform: translateY(-10px) scale(0.95); }
            100% { transform: translateY(0) scale(1); opacity: 1; }
          }

          /* REDUCED RED RADIUS TO 300px */
          @keyframes expandRed {
            0% { width: 0px; height: 0px; opacity: 0.8; }
            100% { width: 300px; height: 300px; opacity: 0.2; }
          }
          @keyframes expandYellow {
            0% { width: 0px; height: 0px; opacity: 0.8; }
            100% { width: 120px; height: 120px; opacity: 0.2; }
          }
          @keyframes expandGreen {
            0% { width: 0px; height: 0px; opacity: 0.8; }
            100% { width: 100px; height: 100px; opacity: 0.2; }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translate(-50%, 10px); }
            to { opacity: 1; transform: translate(-50%, 0); }
          }

          @keyframes sceneFade {
            0% { opacity: 0; }
            95% { opacity: 0; }
            100% { opacity: 1; }
          }

          .search-box {
            position: absolute;
            top: 30px;
            left: 40px;
            background: white;
            padding: 12px 16px;
            border-radius: 8px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.15);
            width: 320px;
            font-family: monospace;
            font-size: 16px;
            color: #333;
            display: flex;
            align-items: center;
            z-index: 10;
          }
          
          .typewriter {
            overflow: hidden;
            white-space: nowrap;
            margin: 0;
            margin-left: 10px;
            animation: typing 1.5s steps(30, end) 0.5s forwards;
            width: 0;
          }

          .pin-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 50px;
            height: 70px;
            position: relative; 
          }

          .pin-body {
            width: 50px;
            height: 50px;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 2px 5px 10px rgba(0,0,0,0.3);
          }

          .pin-icon {
            transform: rotate(45deg);
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .blast-radius {
            position: absolute;
            top: 50%; left: 50%;
            transform: translate(-50%, -50%);
            border-radius: 50%;
            z-index: -1;
            pointer-events: none;
          }
          
          .radius-red {
            background-color: rgba(211, 47, 47, 0.2);
            border: 2px solid #D32F2F;
          }
          .radius-yellow {
            background-color: rgba(251, 192, 45, 0.2);
            border: 2px solid #FBC02D;
          }
          .radius-green {
            background-color: rgba(56, 142, 60, 0.2);
            border: 2px solid #388E3C;
          }

          .risk-tooltip {
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: white;
            padding: 8px 12px;
            border-radius: 6px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
            white-space: nowrap;
            font-family: "Open Sans", sans-serif;
            font-weight: 700;
            font-size: 13px;
            color: #2a2a33;
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 6px;
            z-index: 200; 
          }
          .risk-tooltip::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -6px;
            border-width: 6px;
            border-style: solid;
            border-color: white transparent transparent transparent;
          }

          .fade-overlay {
            position: absolute;
            inset: 0;
            background: white;
            pointer-events: none;
            z-index: 999;
          }
        `}</style>

        <div key={resetKey} style={{ width: '100%', height: '100%', position: 'relative' }}>
          
          <Map
            defaultCenter={{ lat: POS_HOME.lat, lng: POS_HOME.lng + -0.15 }} 
            defaultZoom={12.8} 
            styles={MINIMAL_MAP_STYLES}
            disableDefaultUI={true}
            gestureHandling={'none'}
            style={{ width: '100%', height: '100%' }}
          >
             <CameraAnimator active={true} />

             {/* 1. HOME PIN (Blue) */}
             <HTMLMarker position={POS_HOME} zIndex={100}>
                <div className="pin-container" style={{ opacity: 0, animation: 'pinDrop 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) 4.5s forwards' }}>
                   <div className="pin-body" style={{ backgroundColor: '#1976D2' }}>
                      <div className="pin-icon"><HomeIcon /></div>
                   </div>
                </div>
             </HTMLMarker>

             {/* 2. RISK PIN (Red) */}
             <HTMLMarker position={POS_RISK} zIndex={50}>
                <div className="pin-container" style={{ opacity: 0, animation: 'pinDrop 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) 5.0s forwards' }}>
                   <div className="risk-tooltip" style={{ opacity: 0, animation: 'fadeIn 0.5s ease-out 6.5s forwards' }}>
                      <span style={{ color: '#D32F2F' }}>⚠️</span> Approved Project: Apartments (Traffic Risk)
                   </div>
                   {/* Reduced Radius 300px */}
                   <div className="blast-radius radius-red" style={{ animation: 'expandRed 4s ease-out 5.5s forwards' }}></div>
                   <div className="pin-body" style={{ backgroundColor: '#D32F2F' }}>
                      <div className="pin-icon"><AlertIcon /></div>
                   </div>
                </div>
             </HTMLMarker>

             {/* 3. VOTE PIN (Yellow) */}
             <HTMLMarker position={POS_VOTE} zIndex={50}>
                <div className="pin-container" style={{ opacity: 0, animation: 'pinDrop 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) 5.5s forwards' }}>
                   <div className="risk-tooltip" style={{ opacity: 0, animation: 'fadeIn 0.5s ease-out 7.0s forwards' }}>
                      <span style={{ color: '#FBC02D', fontSize: '14px' }}>?</span> Neighborhood Vote: Large Property Expansion
                   </div>
                   <div className="blast-radius radius-yellow" style={{ animation: 'expandYellow 4s ease-out 6.5s forwards' }}></div>
                   <div className="pin-body" style={{ backgroundColor: '#FBC02D' }}>
                      <div className="pin-icon"><QuestionIcon /></div>
                   </div>
                </div>
             </HTMLMarker>

             {/* 4. ACTIVE PIN (Green) */}
             <HTMLMarker position={POS_ACTIVE} zIndex={50}>
                <div className="pin-container" style={{ opacity: 0, animation: 'pinDrop 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) 6.0s forwards' }}>
                   <div className="risk-tooltip" style={{ opacity: 0, animation: 'fadeIn 0.5s ease-out 7.5s forwards' }}>
                      <span style={{ color: '#388E3C' }}>✓</span> Active Project: Home Addition
                   </div>
                   <div className="blast-radius radius-green" style={{ animation: 'expandGreen 4s ease-out 7.0s forwards' }}></div>
                   <div className="pin-body" style={{ backgroundColor: '#388E3C' }}>
                      <div className="pin-icon"><CraneIcon /></div>
                   </div>
                </div>
             </HTMLMarker>

          </Map>

          <div className="search-box">
            <SearchIcon />
            <p className="typewriter">742 Evergreen Terrace...</p>
          </div>

          <div className="fade-overlay" style={{ animation: `sceneFade ${LOOP_DURATION}ms linear forwards` }}></div>

        </div>
      </div>
    </APIProvider>
  );
};

export default LiveDemoMap;